import React, { useCallback, useMemo } from "react";

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { useHistory, useParams } from "react-router-dom";
import { toast } from "sonner";

import { displayColumns, getDisplayHref } from "./tableParams/displayColumns";
import SmartlistAPI from "../../api/SmartlistAPI";
import ItemStatus from "../../utils/ItemStatus";
import CappedPage from "../common/CappedPage";
import CreateButton from "../common/CreateButton";
import DataTable from "../dataTable/DataTable";
import { PROJECTS_ROUTE } from "../routes/routes";
import Breadcrumbs from "../utils/Breadcrumbs";

import type { JSX } from "react";

import type { DisplayRow } from "./tableParams/displayColumns.types";

function DisplaysList(): JSX.Element {
  const { t } = useTranslation("editor-displays");
  const { projectSlug } = useParams<{ projectSlug: string }>();
  const queryClient = useQueryClient();
  const history = useHistory();

  const getDisplayListQuery = SmartlistAPI.generateQuery({
    fn: SmartlistAPI.getDisplayList,
    params: [{ projectSlug }],
  });

  const {
    data: displaysData,
    isLoading,
    isError,
    refetch,
  } = useQuery(getDisplayListQuery);

  const displayRowList = useMemo(
    (): DisplayRow[] =>
      (displaysData?.data ?? []).map((display) => ({
        ...display,
        status: ItemStatus.getSmartlistStatus(display),
      })),
    [displaysData?.data],
  );

  const { mutate: deleteDisplay } = useMutation({
    mutationFn: async (uuid: string) =>
      SmartlistAPI.deleteDisplay({ uuid, projectSlug }),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: getDisplayListQuery.queryKey,
      });
      toast.success(t("deleteComplete"));
    },
    onError: () => {
      toast.error(t("genericError", { ns: "error" }));
    },
  });

  const getRowHref = useCallback(
    (display: DisplayRow): string => getDisplayHref(projectSlug, display.uuid),
    [projectSlug],
  );

  const columnList = useMemo(
    () =>
      displayColumns({
        t,
        projectSlug,
        onEdit: (display) => history.push(getRowHref(display)),
        onDelete: (display) => deleteDisplay(display.uuid),
      }),
    [t, projectSlug, history, getRowHref, deleteDisplay],
  );

  const breadcrumbs = [
    {
      name: t("editor-menu:publishing-management"),
      url: "/displays",
    },
    {
      name: t("sectionTitle"),
    },
  ];

  const createButton = (
    <CreateButton
      href={`${PROJECTS_ROUTE}/${projectSlug}/displays/new/`}
      label={t("createDisplay")}
    />
  );

  return (
    <CappedPage contentClassName="pt-7.5 pb-15">
      <Breadcrumbs items={breadcrumbs} />
      <DataTable
        columns={columnList}
        data={displayRowList}
        defaultSorting={{ id: "updated_at", desc: true }}
        getRowHref={getRowHref}
        headerActions={createButton}
        isError={isError}
        isLoading={isLoading}
        onRetry={refetch}
        title={t("sectionTitle")}
      />
    </CappedPage>
  );
}

export default DisplaysList;
